import { Formik } from 'formik';
import _ from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import * as Yup from 'yup';
import {
  Button,
  Buttons,
  CancelButton,
  DeleteButton,
  Drawer,
  Field,
  Form,
  FormMessage,
  Icon,
  Tooltip,
} from '~/components';
import ReadTextbox from '~/components/read-only/ReadTextbox';
import { useApi, useWorkspace } from '~/contexts';
import { useDirtyCheck, useDocumentTitle, useForm } from '~/hooks';
import { ErrorPage } from '~/routes/public/pages';
import { colors } from '~/styles';
import { emptyStringToNull, mergeValues } from '~/utils';

const ProjectWrapper = styled.div`
  flex-grow: 1;
`;

const ViewProjectIcon = styled(Icon)`
  left: calc(100% + 1rem);
  position: absolute;
  margin-top: 0.75rem;
  color: ${colors.grey25};

  &:hover {
    color: ${colors.grey55};
    cursor: pointer;
  }
`;

function OpportunityProjectsForm({ opportunity, availableAmount, onSaved, onDeleted, onClose }) {
  useDocumentTitle('Edit Opportunity Project');

  const api = useApi();
  const { workspace } = useWorkspace();
  const [query, setQuery] = useState({ isReady: false, project: null, error: null });
  const [{ status, message, isSubmitting }, form] = useForm();
  const { projectId, projectTab } = useParams();

  const formRef = useRef();
  const dirtyCheck = useDirtyCheck(() => formRef.current.dirty);

  const fetchData = useCallback(async () => {
    try {
      const { data: project } = await api.www
        .workspaces(workspace.id)
        .opportunities(opportunity.id)
        .projects(projectId)
        .get();

      setQuery({ isReady: true, project });
    } catch (error) {
      setQuery({ isReady: true, project: null, error });
    }
  }, [workspace.id, api, projectId, opportunity]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  function handleClose() {
    if (onClose) {
      onClose(projectTab);
    }
  }

  const project = query.project;
  const limitAmount = _.isNumber(availableAmount) ? availableAmount + (project?.amount || 0) : null;

  const handleDelete = async () => {
    onDeleted(projectId);
  };

  if (!query.isReady) return null;
  if (!project) return <ErrorPage.NotFound />;
  if (!project.permissions.edit || !opportunity.permissions.edit) return <ErrorPage.Forbidden publicSite={false} />;

  const initialValues = mergeValues(
    {
      amount: '',
    },
    project,
  );

  return (
    <Drawer
      isOpen
      title="Edit Opportunity Project"
      byline={project?.name}
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={handleClose}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        async function handleSubmit(values) {
          try {
            form.submit();
            const body = emptyStringToNull({
              amount: values.amount,
            });

            await api.www.workspaces(workspace.id).opportunities(opportunity.id).projects(projectId).update(body);
            await onSaved();
            closeDrawer();
          } catch ({ message }) {
            form.error({ message });
          }
        }

        return (
          <Formik
            innerRef={formRef}
            enableReinitialize
            initialValues={initialValues}
            onSubmit={handleSubmit}
            validateOnBlur={false}
            validateOnChange={false}
            validationSchema={Yup.object().shape({
              amount: Yup.number()
                .label('Amount')
                .min(0)
                .max(
                  _.isNumber(limitAmount) ? limitAmount : 99999999999,
                  _.isNumber(limitAmount) ? 'This project amount exceeds the available amount.' : undefined,
                )
                .nullable(),
            })}>
            {(formik) => {
              return (
                <Form>
                  {status && <FormMessage.Error spaceBottom>{message}</FormMessage.Error>}

                  <Form.Section title="Opportunity Project Details">
                    <Form.Control>
                      <div style={{ display: 'flex' }}>
                        <ProjectWrapper>
                          <ReadTextbox label="Project" value={project.name} />
                        </ProjectWrapper>
                        <a
                          href={`/app/${workspace.key}/portfolio/projects/${project.client.key}/${project.key}`}
                          target="_blank"
                          rel="noopener noreferrer">
                          <Tooltip message={'View Project'} placement="top">
                            <ViewProjectIcon icon="external-link-alt" />
                          </Tooltip>
                        </a>
                      </div>
                    </Form.Control>
                    <Form.Control>
                      <Field.Currency name="amount" placeholder="Amount" currency={project.currency} autoFocus />
                    </Form.Control>
                  </Form.Section>

                  <Drawer.Actions>
                    {project && <DeleteButton onClick={handleDelete}>Remove</DeleteButton>}

                    <Buttons align="right">
                      <Buttons align="right">
                        <CancelButton onClick={handleCloseClick}>Close</CancelButton>

                        <Button isLoading={isSubmitting} onClick={formik.submitForm}>
                          Save &amp; Close
                        </Button>
                      </Buttons>
                    </Buttons>
                  </Drawer.Actions>
                </Form>
              );
            }}
          </Formik>
        );
      }}
    </Drawer>
  );
}

export default OpportunityProjectsForm;
